import { emptyHealth, HealthReadError, readSyncedHealth, type SyncedHealth } from './healthClient.ts';
export type ConnectionState = { csrf: string; expiresAt: number; credentials: { id: string; created_at: number }[]; data: SyncedHealth; healthUnavailable: boolean };
export class AccountReadError extends Error {
  readonly status: number;
  constructor(status: number) { super('Account unavailable'); this.status = status; }
}
// Auth and account management remain available while health is disabled/unavailable.
export async function readConnectionState(signal: AbortSignal, fetcher: typeof fetch = fetch): Promise<ConnectionState> {
  async function account(path: string) {
    const response = await fetcher(`/api/auth/${path}`, { credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal });
    if (!response.ok) throw new AccountReadError(response.status);
    return response.json();
  }
  const status = await account('status');
  if (status.authenticated !== true || !/^[A-Za-z0-9_-]{43}$/.test(status.csrf) || !Number.isFinite(status.expiresAt) || status.expiresAt <= Date.now()) throw new Error('Sign in again');
  const keys = await account('credentials');
  if (!Array.isArray(keys.credentials) || keys.credentials.length > 5 || keys.credentials.some((key: { id?: unknown; created_at?: unknown }) => typeof key.id !== 'string' || key.id.length > 1400 || !Number.isFinite(key.created_at))) throw new Error('Account unavailable');
  let data = emptyHealth; let healthUnavailable = status.healthEnabled !== true;
  if (!healthUnavailable) {
    try { data = await readSyncedHealth(null, signal, fetcher); }
    catch (error) {
      if (signal.aborted || (error instanceof HealthReadError && [401, 403].includes(error.status))) throw error;
      healthUnavailable = true;
    }
  }
  if (signal.aborted || status.expiresAt <= Date.now()) throw new Error('Session expired');
  return { csrf: status.csrf, expiresAt: status.expiresAt, credentials: keys.credentials, data, healthUnavailable };
}
